// @flow strict

import * as React from 'react'
import Helmet from 'react-helmet'
import favicon from '../static/favicon.png'
import { type PropsWithChildren, type Obj } from '../utils/typeUtils'

// Note: Flow doesn't support default values in destructuring so
// we need to prepare it for undefineds 🙄
type SEOHeadProps = {
  pageTitle?: ?string,
  description?: ?string,
  imageUrl?: ?string,
  pageUrl?: ?string,
  locale?: ?string,
  ogTitle?: ?string,
  canonicalUrl?: string,
  noindex?: ?boolean,
}

// this is to make react happy and Matt unhappy (unique & stable keys on iterated children, a name that grinds his gears)
const firstValueOfFirstKey = object => Object.values(object)[0]

const generateMetaTags = (metaTags: Obj<string, ?string>[]) =>
  metaTags.map(metaProps => (
    <meta key={firstValueOfFirstKey(metaProps)} {...metaProps} />
  ))

const constantMetaTags = [
  {
    name: 'viewport',
    content: 'width=device-width, initial-scale = 1.0, maximum-scale=5.0',
  },
  {
    httpEquiv: 'X-UA-Compatible',
    content: 'IE=edge',
  },
  { name: 'format-detection', content: 'telephone=no' },
  { property: 'twitter:card', content: 'summary_large_image' },
  { property: 'twitter:widgets:csp', content: 'on' },
]

const SEOHead = ({
  pageTitle = 'Monzo | The bank of the future',
  locale = 'en-GB',
  description = 'Monzo is a bank that makes life easier, not harder.',
  imageUrl = 'https://monzo.com/static/images/home/hot-coral-card-render-OG.png',
  pageUrl = 'https://monzo.com',
  ogTitle,
  canonicalUrl,
  noindex,
  children,
}: PropsWithChildren<SEOHeadProps>) => {
  const variableMetaTags = [
    { name: 'description', content: description },
    { name: 'theme-color', content: '#14233C' },
    { property: 'og:title', content: ogTitle || pageTitle },
    { property: 'og:description', content: description },
    { property: 'og:image', content: imageUrl },
    { property: 'og:locale', content: locale },
    { property: 'og:site_name', content: 'Monzo' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: pageUrl },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: imageUrl },
    { property: 'twitter:site', content: '@monzo' },
    { property: 'twitter:url', content: pageUrl },
  ]

  return (
    <Helmet htmlAttributes={{ lang: locale }}>
      <title>{pageTitle}</title>
      <link
        {...{
          rel: 'shortcut icon',
          href: favicon,
          type: 'image/png',
        }}
      />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex" />}
      {generateMetaTags(constantMetaTags)}
      {generateMetaTags(variableMetaTags)}
      {children}
    </Helmet>
  )
}

SEOHead.defaultProps = {}

export default SEOHead
